import type { ReactNode } from 'react';
import type { TripView } from '@voll-intelligence/domain';
import {
  calendarDateFromUtcDay,
  formatTripRange,
  money,
  tripName,
} from '@voll-intelligence/trip-panel';

import { t } from '@web/i18n';
import { AppIcons, Button } from '@web/ui/core/atoms';
import { useCardTripOf, type CardTripState } from '@web/ui/core/hooks';
import { TripItineraryWithVoucher } from '@web/ui/core/organisms';

import { useConversationCard } from '../hooks/use_conversation_card';

/**
 * The trip on top of the right half: the one picked in the table (the
 * conversation's own row included, labelled as such), else the conversation's
 * own (`useCardTripOf`: card → conversation `tripId` → trip).
 */
export function SelectedTripSection({
  conversationId,
  selectedTrip,
  conversationTripId,
}: Readonly<{
  conversationId: string;
  selectedTrip: TripView | undefined;
  conversationTripId: string | undefined;
}>): React.JSX.Element {
  const conversationTrip = useCardTripOf(useConversationCard(conversationId), conversationId);
  const eyebrow = t('travelerView.trip.conversationEyebrow');
  if (selectedTrip) {
    return (
      <TripPanel
        trip={selectedTrip}
        eyebrow={
          selectedTrip.id === conversationTripId ? eyebrow : t('travelerView.trip.selectedEyebrow')
        }
      />
    );
  }
  if (conversationTrip.status === 'ready') {
    return <TripPanel trip={conversationTrip.trip} eyebrow={eyebrow} />;
  }
  return (
    <SectionFrame eyebrow={eyebrow}>
      <TripStateNotice state={conversationTrip} />
    </SectionFrame>
  );
}

function SectionFrame({
  eyebrow,
  children,
}: Readonly<{ eyebrow: string; children: ReactNode }>): React.JSX.Element {
  return (
    <section
      aria-label={eyebrow}
      className="flex min-w-0 flex-col gap-3 rounded-lg border border-border-soft bg-surface px-4 py-3"
    >
      <header className="flex items-center gap-2">
        <AppIcons name="airplane" size={14} className="text-faint-foreground" />
        <span className="font-mono text-tiny font-medium uppercase tracking-eyebrow-wide text-muted-foreground">
          {eyebrow}
        </span>
      </header>
      {children}
    </section>
  );
}

function TripStateNotice({
  state,
}: Readonly<{ state: Exclude<CardTripState, { status: 'ready' }> }>): React.JSX.Element {
  if (state.status === 'none') {
    return <p className="m-0 text-meta text-muted-foreground">{t('travelerView.trip.none')}</p>;
  }
  if (state.status === 'loading') {
    return <p className="m-0 text-meta text-muted-foreground">{t('card.trip.loading')}</p>;
  }
  return (
    <div className="flex flex-col items-start gap-3">
      <p className="m-0 text-meta text-tone-red-fg">{t('card.trip.loadError')}</p>
      <Button variant="outline" size="sm" onClick={state.retry}>
        {t('card.trip.retry')}
      </Button>
    </div>
  );
}

function TripPanel({
  trip,
  eyebrow,
}: Readonly<{ trip: TripView; eyebrow: string }>): React.JSX.Element {
  // Day-only dates at UTC midnight: converted so a negative zone does not print
  // the range a day early (same as the card trip column).
  const range = formatTripRange(
    calendarDateFromUtcDay(trip.startAt),
    calendarDateFromUtcDay(trip.endAt),
  );
  return (
    <SectionFrame eyebrow={eyebrow}>
      <header className="flex items-baseline justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          <h3 className="m-0 text-base font-medium text-foreground">{tripName(trip)}</h3>
          {range ? <p className="m-0 text-meta text-muted-foreground">{range}</p> : null}
        </div>
        <span className="text-meta text-muted-foreground">
          {money(trip.totalAmount, trip.currency) ?? ''}
        </span>
      </header>
      <TripItineraryWithVoucher trip={trip} className="min-w-0" />
    </SectionFrame>
  );
}
